import levenshtein from 'fast-levenshtein';
import { esStopwords } from '../commons/es_stopwords';

export type NodeItem = {
    id: number;
    value: number;
    label: string;
};

export type EdgeItem = { from: number; to: number; value: number; }

const CHARACTER_SHARING_PERCENTAGE = 60;

export function removeNonConnectedNodes(nodes: NodeItem[], edges: EdgeItem[]) {
    console.log('Cleaning orphaned nodes ...')
    console.log('    Initial nodes ' + nodes.length)
    const idNodesInEdges = new Set()
    const idNodes = new Set()

    nodes.forEach(node => idNodes.add(node.id))

    for (const edge of edges) {
        if (!idNodes.has(edge.from) || !idNodes.has(edge.to))
            continue
        idNodesInEdges.add(edge.from)
        idNodesInEdges.add(edge.to)
    }
    const finalNodes = nodes.filter(n => idNodesInEdges.has(n.id))
    console.log('    Final nodes ' + finalNodes.length)

    return finalNodes
}

export function mergeSimilarNodes(nodes, edges) {
    console.log('Merge similar nodes ...')
    console.log('    Initial nodes ' + nodes.length)
    console.log('    Initial edges ' + edges.length)
    let resNodes = nodes
    let resEdges = edges
    for (let i = 0; i < nodes.length - 1; i++) {
        const nI = nodes[i]
        for (let j = i + 1; j < nodes.length; j++) {
            const nJ = nodes[j]
            if (canBeMerged(nI, nJ)) {
                const tmp = mergeNodes(nI, nJ, resNodes, resEdges)
                resNodes = tmp[0]
                resEdges = tmp[1]
            }
        }
    }
    console.log('    Final nodes ' + resNodes.length)
    console.log('    Final edges ' + resEdges.length)
    return [resNodes, resEdges]
}

/**
 * Merges the edges that has the same pairs of (from, to)
 * @param edges Edges to merge.
 * @returns Merged edges.
 */
export function mergeSimilarEdges(edges: EdgeItem[]): EdgeItem[] {
    console.log('Merging similar edges ...')
    console.log('    Initial edges: ' + edges.length)
    const result: EdgeItem[] = [];

    for (const edge of edges) {
        let found = false
        for (const resEdge of result) {
            if (found) break
            if ((resEdge.from === edge.from && resEdge.to === edge.to)
                || (resEdge.from === edge.to && resEdge.to === edge.from)) {
                resEdge.value += edge.value
                found = true
            }
        }
        if (!found) {
            result.push(edge)
        }
    }
    console.log('    Final edges: ' + result.length)
    return result
}

function mergeNodes(n1: NodeItem, n2: NodeItem, nodes, edges) {
    let nKeep: NodeItem
    let nDel: NodeItem

    if (n1.label.length > n2.label.length) {
        nKeep = n1
        nDel = n2
    } else {
        nKeep = n2
        nDel = n1
    }
    const resNodes = []
    for (const n of nodes) {
        if (n.id !== nDel.id) {
            if (n.id === nKeep.id)
                n.value = nKeep.value + nDel.value
            resNodes.push(n)
        }
    }
    for (const e of edges) {
        if (e.from === nDel.id && e.to !== nKeep.id)
            e.from = nKeep.id
        else if (e.to === nDel.id && e.from !== nKeep.id)
            e.to = nKeep.id
    }
    return [resNodes, edges]
}


function canBeMerged(n1: NodeItem, n2: NodeItem): boolean {
    const n1l = n1.label.toLowerCase()
    const n2l = n2.label.toLowerCase()

    // are the same
    if (n1l === n2l) return true

    // one is the plural of the other one
    if (n1l + 's' === n2l || n1l + 'es' === n2l ||
        n2l + 's' === n1l || n2l + 'es' === n1l) return true

    if (shareMostOfCharacters(n1l, n2l, CHARACTER_SHARING_PERCENTAGE))
        return true

    // TODO: This related to distances cannot be applied to numbers like
    // 10 miles , 20 miles
    const dist = levenshtein.get(n1l, n2l)
    const maxLength = Math.max(n1l.length, n2l.length);
    if (dist <= 1
        && (1 - 1 / maxLength >= .8)
        && n1l[0] === n2l[0]) {
        console.log([n1.label, n2.label, dist])
        return true
    }

    // starts with 'word '
    // ends with ' word'
    // contains ' word '
    if (n1l.split(' ').length > 1 && n2l.split(' ').length > 1)
        return false

    if (n1l.startsWith(n2l + ' ') ||
        n1l.endsWith(' ' + n2l) ||
        n1l.includes(' ' + n2l + ' '))
        return true

    if (n2l.startsWith(n1l + ' ') ||
        n2l.endsWith(' ' + n1l) ||
        n2l.includes(' ' + n1l + ' '))
        return true
    return false
}

export function getPartners(idx, list) {
    const res = [];
    for (const pair of list) {
        if (pair[1] === idx) {
            for (const pair2 of list) {
                if (pair[0] === pair2[0] && pair2[1] !== idx) {
                    res.push(pair2[1])
                }
            }
        }
    }
    return res;
}

export function addEdge(from: number, to: number, edges, posCount = 0, negCount = 0) {
    for (const e of edges) {
        if ((e.from === from && e.to === to) || (e.from === to && e.to === from)) {
            e.value++;
            e.posCount += posCount
            e.negCount += negCount
            return;
        }
    }
    const newEdge = { from, to, value: 1, posCount, negCount }
    edges.push(newEdge)
}

export function getEntityValues(response, exclude: string[]) {
    const entities = response.entities
    const items_entities = response.items_entities

    const res = []
    for (const key in entities) {
        if (exclude.indexOf(key) >= 0)
            continue
        for (const val of entities[key]) {
            if (res.findIndex(x => x.toLowerCase() === val.toLowerCase()) === -1)
                res.push(val)
        }
    }

    for (const key in items_entities) {
        if (exclude.indexOf(key) >= 0)
            continue
        for (const val in items_entities[key]) {
            if (res.findIndex(x => x.toLowerCase() === val.toLowerCase()) === -1)
                res.push(val)
        }
    }

    return res
}

function shareMostOfCharacters(t1: string, t2: string, percentage: number) {
    let shortest = ''
    let largest = ''

    // pick shortest and largest
    if (t1.length < t2.length) {
        shortest = t1
        largest = t2
    }
    else if (t1.length === t2.length)
        return false
    else {
        shortest = t2
        largest = t1
    }

    // The shortest one most have at least 2 words.
    if (shortest.split(' ').length < 2) return false

    // The shortest one should be contained in the largest one.
    if (largest.indexOf(shortest) === -1) return false

    // The amount of words in the largest one should be higher than the amount of words in the smallest one.
    if (shortest.split(' ').length >= largest.split(' ').length) return false

    // The shortest one should represent a percentage equal or higher than the specified.
    if (shortest.length / largest.length < percentage / 100) return false

    console.log(['shareMostOfCharacters', shortest, largest, percentage])
    return true
}

export function cleanSuspectNodes(nodes: NodeItem[]): NodeItem[] {
    const res = [];
    for (const node of nodes) {
        const text = node.label.toLowerCase()
        if (isWordOfOneOrTwoLetters(text)
            || lastWordHasOneOrTwoLetters(text)
            || endsWithInvalidSuffixes(text)
            || endsWithAccent(text)
            || hasModalVerb(text)
            || hasSymbols(text)
            || isInvalidWord(text)
            || isNumber(text)
            || isTime(text)
            || isAmountOfSmth(text)
            || hasInvalidWord(text))
            continue
        res.push(node)
    }
    return res
}

function isWordOfOneOrTwoLetters(text: string) {
    return text.length <= 2;
}

function lastWordHasOneOrTwoLetters(text: string) {
    const words = text.split(' ')

    if (words.length > 1 && isWordOfOneOrTwoLetters(words.pop()))
        return true

    return false
}

function endsWithInvalidSuffixes(text: string) {
    return text.endsWith('ando') || text.endsWith('iendo') || text.endsWith('emos')
        || text.endsWith('amos') || text.endsWith('mente') || text.endsWith('irán')
}

function endsWithAccent(text: string) {
    const words = text.split(' ')
    for (const word of words) {
        if (word.endsWith('á')
            || word.endsWith('é')
            || word.endsWith('í')
            || word.endsWith('ó')
            || word.endsWith('ú'))
            return true
    }
    return false
}

function hasModalVerb(text: string) {
    const words = text.split(' ')
    for (const word of words) {
        if (word.endsWith('ar')
            || word.endsWith('er')
            || word.endsWith('ir'))
            return true
    }
    return false
}

function hasSymbols(text: string) {
    return text.indexOf('.') > -1 || text.indexOf(',') > -1
}

export function cleanEdgesBasedOnSize(edges: EdgeItem[]): EdgeItem[] {
    const result: EdgeItem[] = []
    let threshold = 0

    if (edges.length > 10000) threshold = 3
    else if (edges.length > 1000) threshold = 2
    else if (edges.length > 600) threshold = 1

    for (const edge of edges) {
        if (edge.value > threshold)
            result.push(edge)
    }
    return result
}

export function cleanEdgesBasedOnAmount(edges: EdgeItem[], maxAmount: number = 4000): EdgeItem[] {
    console.log('Clean edges based on amount ...')
    console.log('    Initial edges: ' + edges.length)
    if (edges.length <= maxAmount)
        return edges
    const startIdx = edges.length - maxAmount
    edges.sort((e1: EdgeItem, e2: EdgeItem) => e1.value - e2.value)
    const finalEdges = edges.slice(startIdx, edges.length)
    console.log('    Final edges: ' + finalEdges.length)
    return finalEdges
}

function isInvalidWord(text: string) {
    return esStopwords.indexOf(text) >= 0
}

function isNumber(text: string) {
    return /^\d*$/.test(text)
}

function isTime(text: string) {
    return /^\d*:\d*$/.test(text)
}

function isAmountOfSmth(text: string) {
    return /^\d* \w*$/.test(text)
}

function hasInvalidWord(text: string) {
    const invalidWords = ['gracias', 'qué', 'saludos', 'jaja', 'jeje', 'ud', 'además', 'ejemplos', 'alli', 'tambien', 'alguien', 'seguimos']
    for (const word of invalidWords) {
        const regexPattern = new RegExp(word)
        if (regexPattern.test(text)) {
            console.log(['hasInvalidWord', word, text])
            return true
        }
    }
    return false
}

export function buildDataSource(article: any): { position: any; text: any; sentiment: any; topic: any; }[] {
    const res: { position: any; text: any; sentiment: any; topic: any; }[] = []

    for (const [idx, element] of article.comments.entries()) {
        if (idx >= article.sentiments_paragraphs.length || idx >= article.topics_paragraphs)
            break

        const sentiment = article.sentiments_paragraphs[idx].label
        const topic = article.topics_paragraphs[idx][0]
        res.push({
            position: idx + 1,
            text: element,
            sentiment: sentiment === 'positive' ? 'positivo' : 'negativo',
            topic
        })
    }

    return res
}
